import React from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import AlertStepQuery from './AlertStepQuery';
import AlertStepConversation from './AlertStepConversation';
import AlertMeasurementGate from './AlertMeasurementGate';
import { isMeasurementChange } from '../alertSpec';

/**
 * Which step 2 an author gets (PRD-1381).
 *
 * **Two flags, nested, because they are different questions.**
 * `enable_alert_automations` is the whole-feature switch — off means Alerts is
 * not an automation type at all, including the edit path for every alert that
 * already exists. Turning that off to roll back a *step 2 implementation* would
 * be a feature outage, not a rollback. So the conversational surface gets its
 * own flag, checked only when the outer one is already on. That is the same
 * relationship `enable_alert_automations` itself has with
 * `enable_extra_automation_types`, and for the same reason.
 *
 * Deliberately NOT `alert-delivery`: that is a fail-closed delivery gate held
 * closed pending PRD-1259/PRD-1260, and it is inversely coupled to a UI switch —
 * ON to expose this surface would start unattended delivery before those gates
 * close, and OFF to roll the surface back would stop delivery for every existing
 * alert.
 *
 * **The form is the fallback, not just the off state.** Replacing the wizard
 * made authoring depend on a model being reachable, and the form always worked;
 * an outage must not mean nobody can create an alert. So the conversation hands
 * back when the agent is unreachable, and this renders the form — the same
 * component, with the same fields, that the flag-off path renders.
 */
const AlertStepAuthoring = (props) => {
  const conversationalEnabled = useFlag('enable_conversational_alert_authoring');
  const {
    formData,
    isEditMode = false,
    originalAlert = null,
    measurementAcknowledged = false,
    onAcknowledgeMeasurement = null,
    dryRun = null,
  } = props;

  // PRD-1382, rendered HERE rather than inside the conversation.
  //
  // The wizard blocks step 2 on this either way, and a Next that cannot be
  // pressed with nothing on screen saying why is worse than no gate at all. The
  // form path moves the measurement too — its author button regenerates the
  // query from the same non-deterministic endpoint — so scoping the explanation
  // to the conversational path would have left the flag-off path silently stuck.
  const measurementMoved = isEditMode && isMeasurementChange(originalAlert, formData?.alert);

  return (
    <>
      {conversationalEnabled
        ? <AlertStepConversation {...props} FallbackStep={AlertStepQuery} />
        : <AlertStepQuery {...props} />}
      {measurementMoved && (
        <AlertMeasurementGate
          acknowledged={measurementAcknowledged}
          onAcknowledge={onAcknowledgeMeasurement}
          hasPreview={Boolean(dryRun?.result) || Boolean(formData?.alert?.previewRows?.length)}
        />
      )}
    </>
  );
};

export default AlertStepAuthoring;
